import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Modal} from 'bootstrap';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {EmailModel} from '../../../DataModels/email-model';
import {AppConfigService} from '../../../Services/app-config.service';
import {DataService} from '../../../Services/data.service';
import {SysUserModel} from '../../../DataModels/sys-user-model';
import {EntityContactListModel} from '../../../DataModels/entity-contact-list-model';
import {ResponseModel} from '../../../DataModels/response-model';
import {NotificationService} from '../../../Services/notification.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {Subscription} from 'rxjs';
import { EventService} from '../../../Services/event-service';
import {EmailService} from '../../../Services/email.service';

/**
 * SendEmailModalComponent is a modal component responsible for handling email composition and sending functionality.
 * It supports selecting email sources, attaching reports, and managing email content. It integrates with
 * form handling, validation, and external services to send emails with attachments.
 *
 * Features:
 * - Displays a modal for composing and sending emails.
 * - Prefills email fields based on input or pre-defined settings.
 * - Allows file attachments to emails.
 * - Supports dynamic form controls and validation logic.
 * - Manages email composition, including subject, message body, recipients, and attachments.
 * - Notifies users of validation errors or success through a notification service.
 *
 * Implements:
 * - OnInit: Initializes the component and form configuration upon loading.
 * - OnChanges: Detects and acts on input changes to dynamically update the email form.
 * - OnDestroy: Cleans up subscriptions and other resources when the component is destroyed.
 *
 * Dependencies:
 * - FormBuilder: To dynamically create and manage form controls and validations.
 * - NotificationService: To provide user feedback regarding the email sending process.
 * - DataService: For fetching email recipient data.
 * - EmailService: To handle email-sending execution.
 * - AppConfigService: For app-wide configuration settings.
 * - BsToastService: Displays toast messages for user notifications.
 * - EventService: Listens or emits events for application state changes.
 */
@Component({
  selector: 'app-send-email-modal',
  templateUrl: './send-email-modal.component.html',
  styleUrls: ['./send-email-modal.component.css'],
  standalone: false
})
export class SendEmailModalComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('closeButton') closeButton: ElementRef;
  vm: any = {};
  sendEmailForm: UntypedFormGroup;
  submitted = false;
  objEmail = new EmailModel();
  emailsDropdownEntityData: EntityContactListModel[];
  emailsDropdownSysUsrData: SysUserModel[];
  useSysUserTable: boolean;
  useEntityTable: boolean;
  htmlFormattedMsg: string;
  // emailInfo = new EmailModel();
  selectedFiles: File[] = [];
  selectedRecipients: { email: string; label: string }[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private toastMsg: BsToastService,
    private appCfg: AppConfigService,
    private tableDataService: DataService,
    private notifications: NotificationService,
    private eventService: EventService,
    private emailService: EmailService,
  ) {
    this.sendEmailForm = this.formBuilder.group(
      {
        emailSourceSelected: new UntypedFormControl('SystemUser', Validators.required),
        emailSelected: new UntypedFormControl('', Validators.required),
        // have to use a pattern on email, or it will allow a simple a@b with no dot or extension. Bug I guess.
        emailEntered: new UntypedFormControl(), // ['', [Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')]],
        attachReport: new UntypedFormControl(true),
      },
      {
        // must match stuff etc.
      }
    );
  }

  @Input() emailObject: EmailModel = new EmailModel();
  @Input() autoSendEmail: EmailModel;

  emailToSendArray: EmailModel[] = [];

  subscriptions: Subscription = new Subscription();

  ngOnChanges(changes: SimpleChanges) {
    if (changes['emailObject'] && this.sendEmailForm) {
      this.syncFormFromEmailObject();
    }
    if (changes['autoSendEmail'] && this.autoSendEmail) {
      this.handleAutoSend();
    }
  }

  ngAfterViewInit() {
    const modalEl = this.closeButton?.nativeElement?.closest('.modal');
    if (modalEl) {
      modalEl.addEventListener('show.bs.modal', () => {
        this.sendEmailForm.get('emailSourceSelected').setValue('SystemUser', {emitEvent: false});
        this.loadSelectedSourceEmail();
      });
    }
  }

  ngOnInit() {
    // console.log('send mail form called - these value sent: ' + JSON.stringify(this.emailObject));
    if (!this.emailObject) {
      this.emailObject = new EmailModel();
    }
    this.vm.email = {};
    this.objEmail.includeAttachment = true;

    this.submitted = false;

    if (!this.vm.item) {
      this.vm.item = {};
    }
    if (!this.vm.item.header) {
      this.vm.item.header = {};
    }
    this.onChanges();

    this.syncFormFromEmailObject();

    if (!this.emailObject?.toEmail) {
      this.loadSelectedSourceEmail();
    }

    if (this.autoSendEmail) {
      this.handleAutoSend();
    }
  }

  private handleAutoSend() {
    if (!this.autoSendEmail) {
      return;
    }
    // console.log('Auto-sending email...', this.autoSendEmail);
    // Prepare the payload from autoSendEmail
    const payload = new EmailModel();
    Object.assign(payload, this.autoSendEmail);

    // Fallbacks similar to onSubmit
    if (!payload.fromEmail) {
      payload.fromEmail = this.appCfg.SysEmailAddress;
    }
    if (!payload.fromName) {
      payload.fromName = 'AcctXErp';
    }
    if (!payload.Subject) {
      payload.Subject = $localize`:Information from AcctXERP @@informationfromAcctXERP:Information from AcctXERP`;
    }
    if (!payload.body) {
      payload.body = 'Please see the attached document.';
    }
    // Ensure body is wrapped in div/p if it's plain text (matching onSubmit logic)
    if (payload.body && !payload.body.includes('<div')) {
      payload.body = '<div><p>' + payload.body + '</p></div>';
    }

    if (!payload.toEmail) {
      this.notifications.warning('Auto-send failed: No recipient email address provided.');
      return;
    }

    // Send via email service (auto-send usually won't have manual file uploads from the modal)
    this.emailService.sendEmail({
      FromEmail: payload.fromEmail,
      FromName: payload.fromName,
      ToEmail: payload.toEmail,
      Subject: payload.Subject,
      Body: payload.body,
      PdfHtml: payload.pdfHtml,
      FileName: payload.FileName || (payload.pdfHtml ? 'document.pdf' : ''),
      IsBodyHtml: true,
      header_id: payload.header_id,
      entity_id: payload.entity_id,
      activity_message: payload.activity_message || 'item auto-emailed',
      includeAttachment: payload.includeAttachment !== false,
      PrintOrientation: payload.print_orientation,
      ReportFormatType: payload.convert_attachment_to,
    });

    // We don't need to show or close the modal if it's auto-send
    // But we should cleanup and ensure the parent knows we are done if it was watching for state
    this.cleanupAfterSend();

    // Reset autoSendEmail so it can be triggered again if the input changes back and forth
    // though usually handled by ngOnChanges.
  }

  private syncFormFromEmailObject() {
    if (!this.emailObject) {
      return;
    }

    this.objEmail.header_id = this.emailObject.header_id;
    this.objEmail.entity_id = this.emailObject.entity_id;
    this.objEmail.activity_message = this.emailObject.activity_message;
    this.objEmail.pdfHtml = this.emailObject.pdfHtml;
    this.objEmail.FileName = this.emailObject.FileName;
    this.objEmail.Subject = this.emailObject.Subject;

    // Prefill recipient if parent provided toEmail
    const initialTo = (this as any).emailObject.toEmail || '';
    if (initialTo) {
      const srcCtrl = this.sendEmailForm.get('emailSourceSelected');
      const enteredCtrl = this.sendEmailForm.get('emailEntered');
      const selectedCtrl = this.sendEmailForm.get('emailSelected');
      srcCtrl.setValue('SystemUser', {emitEvent: true});
      enteredCtrl.enable({emitEvent: false});
      enteredCtrl.setValue(initialTo, {emitEvent: false});
      selectedCtrl.disable({emitEvent: false});
      this.setRequiredAndValidators(true);
    }

    if (this.emailObject.body && !this.htmlFormattedMsg) {
      this.htmlFormattedMsg = this.emailObject.body;
    }
  }

  onChanges() {
    let isRequired = false;
    // default list starts on selected so set to required so it inits handlers etc. then flips back
    this.setRequiredAndValidators(true);
    this.subscriptions.add(this.sendEmailForm.controls.emailSourceSelected?.valueChanges.subscribe((val) => {
      // console.log(`Email source is ${val}.`);
      this.submitted = false;
      if (val !== 'Entered') {
        this.sendEmailForm.controls.emailEntered.disable();
        this.sendEmailForm.get('emailEntered').setValue(null);
        this.sendEmailForm.get('emailEntered').markAsPristine();
        this.sendEmailForm.get('emailEntered').markAsUntouched();
        this.sendEmailForm.get('emailSelected').enable();
        this.sendEmailForm.get('emailSelected').markAsPristine();
        this.sendEmailForm.get('emailSelected').markAsUntouched();
        isRequired = false;
      } else if (val === 'Entered') {
        isRequired = true;
        this.sendEmailForm.controls.emailEntered.enable();
        // this.sendEmailForm.get('emailSelected').setValue(null);
        this.sendEmailForm.get('emailEntered').setValue(null);
        this.sendEmailForm.get('emailSelected').disable();
        this.sendEmailForm.get('emailSelected').markAsPristine();
        this.sendEmailForm.get('emailSelected').markAsUntouched();
      }
      this.setRequiredAndValidators(isRequired);
    }));
    // checkbox attach
    this.subscriptions.add(this.sendEmailForm.controls.attachReport?.valueChanges.subscribe((val) => {
     // console.log(`IncludeAttachment checkbox val is ${val}.`);
      // this.objEmail.includeAttachment = val;
    }));
  }

  setRequiredAndValidators(required: boolean) {
    if (required) {
      const emailControl = this.sendEmailForm.get('emailEntered');
      emailControl.setValidators([
        Validators.required,
        Validators.pattern('^([\\w+-.%]+@[\\w.-]+\\.[A-Za-z]{2,4})(,[\\w+-.%]+@[\\w.-]+\\.[A-Za-z]{2,4})*$'),
      ]);
      emailControl.updateValueAndValidity();
    } else {
      const emailControl = this.sendEmailForm.get('emailEntered');
      emailControl.setValidators([
        Validators.pattern('^([\\w+-.%]+@[\\w.-]+\\.[A-Za-z]{2,4})(,[\\w+-.%]+@[\\w.-]+\\.[A-Za-z]{2,4})*$'),
      ]);
      emailControl.updateValueAndValidity();
    }
  }

  // getter for easy access to form fields
  get f() {
    return this.sendEmailForm.controls;
  }

  onFilesSelected(evt: Event) {
    const input = evt.target as HTMLInputElement;
    if (input?.files && input.files.length) {
      this.selectedFiles = [...this.selectedFiles, ...Array.from(input.files)];
      // reset input so selecting the same file again will trigger change
      input.value = '';
    }
  }

  removeFile(index: number) {
    if (index >= 0 && index < this.selectedFiles.length) {
      this.selectedFiles.splice(index, 1);
    }
  }

  onSubjectChange(value: string) {
    if (!this.emailObject) {
      this.emailObject = new EmailModel();
    }
    this.emailObject.Subject = value;
  }

  onMessageChange(value: string) {
    this.htmlFormattedMsg = value;
  }

  private normalizeQuillMessage(value: string): string {
    if (!value) {
      return '';
    }

    const html = value.trim();
    if (!html) {
      return '';
    }

    const text = html
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const hasEmbeddedContent = /<(img|iframe|video|audio|object|embed|svg)\b/i.test(html);
    return text || hasEmbeddedContent ? html : '';
  }

  onSubmit() {
    this.submitted = true;
    if (this.sendEmailForm.invalid) {
      this.toastMsg.showWarningToast('Form Errors');
      return;
    }

    const normalizedMessage = this.normalizeQuillMessage(this.htmlFormattedMsg);
    this.htmlFormattedMsg = normalizedMessage;

    // message/body prep
    if (normalizedMessage) {
      this.emailObject.body = normalizedMessage;
    } else {
      this.emailObject.body = 'Please see the attached document.';
    }

    // To: merge sources (form selection + fallback to provided emailObject)
    this.objEmail.toEmail = '';
    const selected = this.sendEmailForm.get('emailSelected').value;
    const entered = this.sendEmailForm.get('emailEntered').value;
    if (selected) {
      this.objEmail.toEmail = selected;
    }
    if (entered) {
      this.objEmail.toEmail = this.objEmail.toEmail ? `${this.objEmail.toEmail},${entered}` : entered;
    }
    if (!this.objEmail.toEmail && this.emailObject?.toEmail) {
      this.objEmail.toEmail = this.emailObject.toEmail;
    }

    // From and subject
    this.objEmail.fromEmail = this.appCfg.SysEmailAddress;
    this.objEmail.Subject = (this.emailObject.Subject == undefined || this.emailObject.Subject == '')
      ? $localize`:Information from AcctXERP @@informationfromAcctXERP:Information from AcctXERP`
      : this.emailObject.Subject;

    // Body
    if (!normalizedMessage) {
      this.objEmail.body = '<div><p>' + (this.emailObject.body) + '</p></div>';
    } else {
      this.objEmail.body = '<div><p>' + normalizedMessage + '</p></div>';
    }

    this.objEmail.header_id = this.emailObject.header_id;
    this.objEmail.entity_id = this.emailObject.entity_id;
    this.objEmail.toEmail = (this.objEmail.toEmail || '').toString().trim();
    this.objEmail.fromName = this.emailObject.fromName || 'AcctXErp';
    this.objEmail.activity_message = this.emailObject.activity_message || 'item emailed';
    this.objEmail.includeAttachment = this.sendEmailForm.get('attachReport').value;
    this.objEmail.pdfHtml = this.emailObject.pdfHtml;
    if (!this.emailObject.FileName && this.objEmail.pdfHtml) {
      this.objEmail.FileName = 'document.pdf';
    } else {
      this.objEmail.FileName = this.emailObject.FileName;
    }

    if (!this.objEmail.toEmail) {
      const msg = 'Either no email address has been entered or the system item chosen has no email address stored.';
      this.notifications.warning(msg);
      return;
    }

    // Decide which transport to use
    const hasFiles = Array.isArray(this.selectedFiles) && this.selectedFiles.length > 0;
    if (hasFiles) {
      // Use FormData upload when user selected any files in the modal

      this.emailService.sendEmailWithAttachments({
        toEmail: this.objEmail.toEmail,
        fromEmail: this.objEmail.fromEmail,
        fromName: this.objEmail.fromName,
        subject: this.objEmail.Subject,
        body: this.objEmail.body,
        // IDs and meta (send both legacy and preferred keys as supported by the service)
        header_id: this.objEmail.header_id,
        entity_id: this.objEmail.entity_id,
        activity_message: this.objEmail.activity_message,
        includeAttachment: !!this.objEmail.includeAttachment,
        pdfHtml: this.objEmail.pdfHtml,
        FileName: this.objEmail.FileName,
        IsBodyHtml: true,
        printOrientation: this.emailObject.print_orientation,
        convertAttachmentTo: this.emailObject.convert_attachment_to,
      }, this.selectedFiles);
    } else {
      // Use legacy JSON endpoint when no files were selected
      this.emailService.sendEmail({
        FromEmail: this.objEmail.fromEmail,
        FromName: this.objEmail.fromName,
        ToEmail: this.objEmail.toEmail,
        Subject: this.objEmail.Subject,
        Body: this.objEmail.body,
        PdfHtml: this.objEmail.pdfHtml,
        FileName: this.objEmail.FileName,
        IsBodyHtml: true,
        header_id: this.objEmail.header_id,
        entity_id: this.objEmail.entity_id,
        activity_message: this.objEmail.activity_message,
        includeAttachment: !!this.objEmail.includeAttachment,
        PrintOrientation: this.emailObject.print_orientation,
        ReportFormatType: this.emailObject.convert_attachment_to,
      });
    }

    const sentTo = this.objEmail.toEmail;
    this.eventService.emit({
      id: 0,
      name: 'EmailEvent',
      date: new Date(),
      description: 'some data was emailed to:' + sentTo,
      location: 'here',
      action: 'about to send email'
    });

    this.cleanupAfterSend();
  }

  onReset() {
    this.submitted = false;
    this.sendEmailForm.reset();
    this.sendEmailForm.get('emailSourceSelected').setValue('SystemUser', {emitEvent: false});
  }

  isRecipientSelected(email: string): boolean {
    return this.selectedRecipients.some(r => r.email === email);
  }

  toggleRecipient(email: string, label: string): void {
    const idx = this.selectedRecipients.findIndex(r => r.email === email);
    if (idx >= 0) {
      this.selectedRecipients.splice(idx, 1);
    } else {
      this.selectedRecipients.push({email, label});
    }
    this.sendEmailForm.get('emailSelected').setValue(
      this.selectedRecipients.map(r => r.email).join(',')
    );
  }

  removeRecipient(email: string): void {
    this.selectedRecipients = this.selectedRecipients.filter(r => r.email !== email);
    this.sendEmailForm.get('emailSelected').setValue(
      this.selectedRecipients.map(r => r.email).join(',')
    );
  }

  loadSelectedSourceEmail() {
    this.useSysUserTable = false;
    this.useEntityTable = false;
    this.selectedRecipients = [];
    this.sendEmailForm.get('emailSelected').setValue('');

    if (this.sendEmailForm.get('emailSourceSelected').value === 'SystemUser') {
      this.tableDataService
        .getData('sys_user', '?pageNumber=0&pageSize=999999')
        .subscribe((result: ResponseModel) => {
          this.emailsDropdownSysUsrData = result.Data;
        });
      this.useSysUserTable = true;
    }
    if (this.sendEmailForm.get('emailSourceSelected').value === 'EntityContact') {
      this.tableDataService
        .getData('entity_contact_list', 'fulllist?pageNumber=0&pageSize=999999')
        .subscribe((result: ResponseModel) => {
          this.emailsDropdownEntityData = result.Data;
          this.emailsDropdownEntityData = this.emailsDropdownEntityData.sort((a, b) => (a.entity_name > b.entity_name ? 1 : -1));
          // console.log(JSON.stringify(sorted));
        });
      this.useEntityTable = true;
    }
  }

  cleanupAfterSend() {
    this.objEmail = new EmailModel();
    this.htmlFormattedMsg = '';
    this.selectedFiles = [];
    this.selectedRecipients = [];
    this.onReset();
    // close modal if available
    try {
      this.closeButton?.nativeElement?.click();
    } catch {
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    const modalEl = this.closeButton?.nativeElement?.closest('.modal');
    if (modalEl) {
      const modal = Modal.getInstance(modalEl);
      if (modal) {
        modal.hide();
      }
    }

  }

}
